import React, { useState, useEffect } from "react";
import axios from "axios";
import { X, History, ArrowDownLeft, ArrowUpRight, ArrowRightLeft, FileSpreadsheet, RefreshCw } from "lucide-react";

const API = process.env.REACT_APP_API_URL;

const StockLedgerModal = ({ isOpen, onClose, product }) => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchProductActivities = async () => {
    if (!product?._id) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/inventory/activities?limit=50`);
      if (res.data.success) {
        // filter for this specific product or matching name/sku
        const acts = (res.data.activities || []).filter(
          (a) => a.product?._id === product._id || a.sku === product.sku || a.productName === (product.name?.en || product.name)
        );
        setActivities(acts);
      }
    } catch (err) {
      console.error("Error fetching product ledger:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && product) {
      fetchProductActivities();
    }
  }, [isOpen, product]);

  if (!isOpen || !product) return null;

  const prodName = product.name?.en || (typeof product.name === "string" ? product.name : "Product");

  const getTypeBadge = (type) => {
    if (type === "Stock In" || type === "Receipt") {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold bg-green-100 text-green-800 border border-green-200">
          <ArrowDownLeft size={12} /> {type}
        </span>
      );
    }
    if (type === "Stock Out" || type === "Sale") {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold bg-red-100 text-red-800 border border-red-200">
          <ArrowUpRight size={12} /> {type}
        </span>
      );
    }
    if (type === "Transfer") {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold bg-purple-100 text-purple-800 border border-purple-200">
          <ArrowRightLeft size={12} /> {type}
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold bg-blue-100 text-blue-800 border border-blue-200">
        <FileSpreadsheet size={12} /> {type}
      </span>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/60 backdrop-blur-sm overflow-y-auto">
      <div className="bg-white rounded-xl shadow-2xl border border-gray-200 w-full max-w-3xl overflow-hidden animate-in fade-in zoom-in-95 duration-150">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#131921] to-[#232f3e] px-5 py-4 text-white flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center text-[#febd69]">
              <History className="w-5 h-5" />
            </div>
            <div>
              <div className="text-xs font-medium text-[#febd69] uppercase tracking-wider">
                Audit Trail & History
              </div>
              <h3 className="text-base font-semibold text-white leading-tight">
                Inventory Transaction Ledger
              </h3>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={fetchProductActivities}
              className="text-gray-300 hover:text-white p-1.5 rounded-lg hover:bg-white/10 transition"
              title="Refresh"
            >
              <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
            </button>
            <button 
              onClick={onClose}
              className="text-gray-400 hover:text-white p-1.5 rounded-lg hover:bg-white/10 transition"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Product Details Header */}
        <div className="bg-gray-50 border-b border-gray-200 px-5 py-3 flex items-center justify-between gap-4">
          <div>
            <h4 className="text-sm font-bold text-gray-900 truncate max-w-md">{prodName}</h4>
            <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
              <span>SKU: <strong>{product.sku || "--"}</strong></span>
              <span>•</span>
              <span>ID: <strong>{product.bhkg_id || "--"}</strong></span>
              <span>•</span>
              <span>Warehouse: <strong>{product.warehouse || "Bhubaneswar (Main)"}</strong></span>
            </div>
          </div>
          <div className="text-right">
            <span className="text-xs text-gray-500">Current Stock</span>
            <div className="text-base font-bold text-gray-900">{product.stock || 0} units</div>
          </div>
        </div>

        {/* Ledger Table */}
        <div className="max-h-96 overflow-y-auto">
          {loading ? (
            <div className="py-12 text-center text-gray-500 flex flex-col items-center justify-center gap-2">
              <RefreshCw className="w-6 h-6 animate-spin text-[#b07d17]" />
              <span className="text-sm">Loading transaction history...</span>
            </div>
          ) : activities.length > 0 ? (
            <table className="w-full text-left text-xs sm:text-sm">
              <thead className="bg-[#f2f2f2] text-gray-700 sticky top-0 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-2.5 font-semibold">Date & Time</th>
                  <th className="px-4 py-2.5 font-semibold">Txn ID</th>
                  <th className="px-4 py-2.5 font-semibold">Action</th>
                  <th className="px-4 py-2.5 font-semibold text-right">Delta</th>
                  <th className="px-4 py-2.5 font-semibold text-right">Balance</th>
                  <th className="px-4 py-2.5 font-semibold">Warehouse</th>
                  <th className="px-4 py-2.5 font-semibold">Notes / By</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {activities.map((act) => (
                  <tr key={act._id} className="hover:bg-gray-50 transition">
                    <td className="px-4 py-2.5 text-gray-600 whitespace-nowrap">
                      {new Date(act.createdAt).toLocaleString("en-IN", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </td>
                    <td className="px-4 py-2.5 font-mono text-[11px] text-gray-500">
                      {act.transactionId || "--"}
                    </td>
                    <td className="px-4 py-2.5">
                      {getTypeBadge(act.type)}
                    </td>
                    <td className="px-4 py-2.5 font-bold text-right">
                      <span className={act.quantity > 0 ? "text-green-700" : act.quantity < 0 ? "text-red-700" : "text-gray-700"}>
                        {act.quantity > 0 ? `+${act.quantity}` : act.quantity}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-right font-medium text-gray-900">
                      {act.afterQuantity ?? "--"}
                    </td>
                    <td className="px-4 py-2.5 text-gray-600 text-xs">
                      {act.warehouse || "--"}
                      {act.warehouseTo ? ` → ${act.warehouseTo}` : ""}
                    </td>
                    <td className="px-4 py-2.5 text-gray-500 text-xs">
                      <div className="truncate max-w-[150px]" title={act.note}>
                        {act.note || "System update"}
                      </div>
                      <div className="text-[10px] text-gray-400">By: {act.performedBy || "Admin"}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="py-12 text-center text-gray-500">
              <History className="w-10 h-10 text-gray-300 mx-auto mb-2" />
              <p className="text-sm font-medium">No ledger activity logged yet for this SKU.</p>
              <p className="text-xs text-gray-400 mt-0.5">
                Any future restock, transfer, sale, or manual adjustment will automatically appear here.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 border-t border-gray-200 px-5 py-3 flex items-center justify-between">
          <span className="text-xs text-gray-500">
            Showing {activities.length} activity record{activities.length === 1 ? "" : "s"}
          </span>
          <button
            onClick={onClose}
            className="px-4 py-1.5 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg text-sm font-medium transition"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default StockLedgerModal;
